import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

const BILLING_BYPASS_USER_IDS = new Set<string>([
  '7591c3af-8654-4f76-85b9-15d75da78043',
]);

const BILLING_BYPASS_UNTIL_MS = new Date('2026-03-23T23:59:59Z').getTime();

function isBillingBypassed(userId?: string) {
  return !!userId &&
    BILLING_BYPASS_USER_IDS.has(userId) &&
    Date.now() < BILLING_BYPASS_UNTIL_MS;
}

interface BillingContextType {
  billingGateLoading: boolean;
  billingRevalidating: boolean;
  hasActiveBilling: boolean;
  refreshBillingStatus: (options?: { blocking?: boolean }) => Promise<boolean>;
}

const BillingContext = createContext<BillingContextType | undefined>(undefined);

export function BillingProvider({ children }: { children: React.ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const [billingGateLoading, setBillingGateLoading] = useState<boolean>(true);
  const [billingRevalidating, setBillingRevalidating] = useState<boolean>(false);
  const [hasActiveBilling, setHasActiveBilling] = useState<boolean>(false);
  const inFlightCheckRef = useRef<Promise<boolean> | null>(null);
  const hasActiveBillingRef = useRef<boolean>(false);
  const lastBlockingCheckUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    hasActiveBillingRef.current = hasActiveBilling;
  }, [hasActiveBilling]);

  const refreshBillingStatus = useCallback(async (options?: { blocking?: boolean }): Promise<boolean> => {
    if (authLoading) return false;

    if (!user?.id) {
      setHasActiveBilling(false);
      // Keep loading=true while unauthenticated so the next login always starts
      // from a loading state instead of briefly redirecting to billing.
      setBillingGateLoading(true);
      setBillingRevalidating(false);
      lastBlockingCheckUserIdRef.current = null;
      inFlightCheckRef.current = null;
      return false;
    }
    
    if (isBillingBypassed(user.id)) {
      setHasActiveBilling(true);
      setBillingGateLoading(false);
      setBillingRevalidating(false);
      lastBlockingCheckUserIdRef.current = user.id;
      inFlightCheckRef.current = null;
      return true;
    }

    const isBlockingCheck =
      typeof options?.blocking === 'boolean'
        ? options.blocking
        : lastBlockingCheckUserIdRef.current !== user.id;

    if (inFlightCheckRef.current) {
      return inFlightCheckRef.current;
    }

    const checkPromise = (async () => {
      const resolveFromBillingProfile = async (): Promise<'active' | 'inactive' | 'unknown'> => {
        try {
          const { data, error } = await supabase
            .from('billing_profiles')
            .select('status, default_payment_method_id')
            .eq('user_id', user.id)
            .maybeSingle();

          if (error) {
            console.error('Failed billing profile fallback check:', error);
            return 'unknown';
          }

          const active = data?.status === 'active' || !!data?.default_payment_method_id;
          return active ? 'active' : 'inactive';
        } catch (profileError) {
          console.error('Billing profile fallback threw error:', profileError);
          return 'unknown';
        }
      };

      try {
        const previouslyActive = hasActiveBillingRef.current;
        if (isBlockingCheck) {
          setBillingGateLoading(true);
        } else {
          setBillingRevalidating(true);
        }

        let nextStatus: 'active' | 'inactive' | 'unknown' = 'unknown';
        let accessToken: string | undefined;

        // After login, session propagation can lag briefly.
        for (let attempt = 0; !accessToken && attempt < 10; attempt += 1) {
          const { data: { session } } = await supabase.auth.getSession();
          accessToken = session?.access_token;
          if (accessToken) break;
          if (attempt < 9) {
            await new Promise((resolve) => setTimeout(resolve, 250));
          }
        }

        if (!accessToken) {
          nextStatus = await resolveFromBillingProfile();
        } else {
          const response = await fetch('/api/billing/upcoming-due', {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          });

          const payload = await response.json().catch(() => ({}));
          if (!response.ok) {
            throw new Error(payload?.error || `Failed billing check (${response.status})`);
          }

          const apiActive = !!payload?.hasActiveBilling;
          if (apiActive) {
            nextStatus = 'active';
          } else {
            // If API says inactive but local billing profile is active, trust profile
            // so users with valid billing are not incorrectly redirected.
            const profileStatus = await resolveFromBillingProfile();
            nextStatus = profileStatus === 'active' ? 'active' : 'inactive';
          }
        }

        // During background checks, never interrupt an active in-progress session
        // based on inconclusive/transient checks.
        if (!isBlockingCheck && previouslyActive && nextStatus !== 'active') {
          if (nextStatus === 'unknown') {
            console.warn('Billing revalidation inconclusive; preserving active access');
          } else {
            console.warn('Billing revalidation reported inactive; preserving active access until next blocking check');
          }
          return true;
        }

        const nextActive = nextStatus === 'active';
        setHasActiveBilling(nextActive);
        if (isBlockingCheck) {
          lastBlockingCheckUserIdRef.current = user.id;
        }
        return nextActive;
      } catch (error) {
        console.error('Failed billing gate check:', error);
        const fallbackStatus = await resolveFromBillingProfile();
        if (!isBlockingCheck && hasActiveBillingRef.current && fallbackStatus !== 'active') {
          console.warn('Billing revalidation failed; preserving active access');
          return true;
        }
        const nextActive = fallbackStatus === 'active';
        setHasActiveBilling(nextActive);
        if (isBlockingCheck) {
          lastBlockingCheckUserIdRef.current = user.id;
        }
        return nextActive;
      } finally {
        if (isBlockingCheck) {
          setBillingGateLoading(false);
        } else {
          setBillingRevalidating(false);
        }
        inFlightCheckRef.current = null;
      }
    })();

    inFlightCheckRef.current = checkPromise;
    return checkPromise;
  }, [authLoading, user?.id]);

  useEffect(() => {
    if (authLoading) return;

    if (!user?.id) {
      setHasActiveBilling(false);
      setBillingGateLoading(true);
      setBillingRevalidating(false);
      lastBlockingCheckUserIdRef.current = null;
      inFlightCheckRef.current = null;
      return;
    }

    void refreshBillingStatus({ blocking: true });
  }, [authLoading, user?.id, refreshBillingStatus]);

  return (
    <BillingContext.Provider
      value={{
        billingGateLoading,
        billingRevalidating,
        hasActiveBilling,
        refreshBillingStatus,
      }}
    >
      {children}
    </BillingContext.Provider>
  );
}

export function useBilling() {
  const context = useContext(BillingContext);
  if (!context) {
    throw new Error('useBilling must be used within a BillingProvider');
  }
  return context;
}
